// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary

import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert, Typography,
} from '@mui/material';
import { changeOwnPassword, getSession } from './auth';

// Self-service password change for whoever is signed in. Deliberately separate
// from the admin reset in UserAdmin: this one proves possession of the CURRENT
// password, so it needs no privilege and is available to every role, including
// a viewer who can change nothing else.

// Mirrors the pool policy in cdk/lib/auth-stack.ts. Checked here only to fail
// fast with a readable message — Cognito remains the authority.
const POLICY = 'At least 8 characters, with an uppercase letter, a lowercase letter, and a number.';
const meetsPolicy = (p) => p.length >= 8 && /[a-z]/.test(p) && /[A-Z]/.test(p) && /\d/.test(p);

function errorMessage(e) {
  switch ((e && e.name) || '') {
    case 'NotAuthorizedException':
      // Cognito uses this both for a wrong current password and for a dead
      // token. The message has to cover the first without misleading on the
      // second, because the user can act on one and not the other.
      return 'That current password is not correct. If you have been signed in a long time, '
        + 'sign out and back in, then try again.';
    case 'InvalidPasswordException':
      return `That new password does not meet the requirements. ${POLICY}`;
    case 'LimitExceededException':
    case 'TooManyRequestsException':
      return 'Too many attempts. Wait a few minutes before trying again.';
    default:
      return (e && e.message) || 'Could not change your password.';
  }
}

export default function ChangePassword({ open, onClose }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const session = getSession();
  // A federated user has no Cognito password to change — theirs lives in Entra.
  // Offering the form would hand them a control that can only ever fail.
  const federated = !!session && session.sso;

  const close = () => {
    setCurrent(''); setNext(''); setConfirm(''); setErr(''); setDone(false);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (next !== confirm) { setErr('The new passwords do not match.'); return; }
    if (next === current) { setErr('Your new password must be different from your current one.'); return; }
    if (!meetsPolicy(next)) { setErr(`That new password does not meet the requirements. ${POLICY}`); return; }
    setBusy(true);
    try {
      await changeOwnPassword(current, next);
      // Drop both passwords from state the moment they are no longer needed.
      setCurrent(''); setNext(''); setConfirm('');
      setDone(true);
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={() => !busy && close()} fullWidth maxWidth="xs">
      <DialogTitle sx={{ fontWeight: 700, color: '#021F4B' }}>Change your password</DialogTitle>
      <form onSubmit={submit}>
        <DialogContent dividers>
          {federated ? (
            <Alert severity="info">
              You sign in with your Optimum work account, so your password is managed by Optimum,
              not by Primordia. Change it where you change your other work passwords.
            </Alert>
          ) : done ? (
            <Alert severity="success">
              Your password has been changed. You will stay signed in here; use the new password
              next time.
            </Alert>
          ) : (
            <>
              {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
              <Typography sx={{ fontSize: 13, color: '#5C6B7A', mb: 2 }}>
                Signed in as
                {' '}
                <b>{session ? session.email : ''}</b>
                .
              </Typography>
              {/* The account this password belongs to, so a password manager
                  can offer to update the right entry rather than nothing. */}
              <input type="text" name="username" autoComplete="username"
                value={session ? session.email : ''} readOnly hidden aria-hidden="true"
                tabIndex={-1} onChange={() => {}} />
              <TextField fullWidth size="small" type="password" label="Current password" required
                value={current} autoFocus onChange={(e) => setCurrent(e.target.value)} sx={{ mb: 2 }}
                name="current-password" inputProps={{ autoComplete: 'current-password' }} />
              <TextField fullWidth size="small" type="password" label="New password" required
                value={next} onChange={(e) => setNext(e.target.value)} sx={{ mb: 2 }}
                inputProps={{ autoComplete: 'new-password' }} helperText={POLICY} />
              <TextField fullWidth size="small" type="password" label="Confirm new password" required
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                inputProps={{ autoComplete: 'new-password' }} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={close} disabled={busy}>{done || federated ? 'Close' : 'Cancel'}</Button>
          {!done && !federated && (
            <Button type="submit" variant="contained" disabled={busy || !current || !next || !confirm}
              sx={{ bgcolor: '#0D5B9A', '&:hover': { bgcolor: '#021F4B' } }}>
              {busy ? 'Changing…' : 'Change password'}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
}
